import { User } from 'lucide-react';
import {
  formatMobileDisplay,
  formatMobileWithCountryCode,
  getMobileConfigByCode,
  parseMobileInput,
} from '@/features/auth/utils/mobileNumber';
import { inputValidation } from '@/utils/function';
import { useIsClient } from '@/shared/hooks/useIsClient';
import { Field } from '../../ProfilePagePrimitives';
import type { ProfileOverviewForm } from '../../ProfilePage.types';

type PersonalInfoSectionProps = {
  form: ProfileOverviewForm;
  editing: boolean;
  setForm: React.Dispatch<React.SetStateAction<ProfileOverviewForm>>;
  nameErrors?: { firstName?: string; lastName?: string };
  onClearNameError?: (field: 'firstName' | 'lastName') => void;
};

function splitContactName(value: string) {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
}

export function PersonalInfoSection({
  form,
  editing,
  setForm,
  nameErrors,
  onClearNameError,
}: PersonalInfoSectionProps) {
  const isClient = useIsClient();
  const mobileConfig = getMobileConfigByCode(form.countryCode);
  const contactPersonName = [form.firstName, form.lastName]
    .filter(Boolean)
    .join(' ');

  const handleMobileChange = (raw: string) => {
    const { value } = parseMobileInput(raw, mobileConfig);
    setForm((f) => ({ ...f, mobileNumber: value }));
  };

  return (
    <section className="rounded-2xl border border-[var(--dash-line)] bg-white p-5 text-left shadow-[0_4px_20px_-10px_rgba(16,38,73,0.08)]">
      <div className="mb-4 flex items-center gap-2">
        <User className="h-4 w-4 text-[var(--dash-brand)]" />
        <h3 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
          Personal Information
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        {isClient ? (
          <div className="sm:col-span-2">
            <Field
              id="profile-firstName"
              label="Key Contact Person Name"
              value={contactPersonName}
              editing={editing}
              required
              maxLength={50}
              error={nameErrors?.firstName}
              onChange={(v) => {
                onClearNameError?.('firstName');
                onClearNameError?.('lastName');
                const next = splitContactName(inputValidation(v).slice(0, 50));
                setForm((f) => ({
                  ...f,
                  firstName: next.firstName,
                  lastName: next.lastName,
                }));
              }}
            />
          </div>
        ) : (
          <>
            <Field
              id="profile-firstName"
              label="First Name"
              value={form.firstName}
              editing={editing}
              required
              maxLength={20}
              error={nameErrors?.firstName}
              onChange={(v) => {
                onClearNameError?.('firstName');
                setForm((f) => ({
                  ...f,
                  firstName: inputValidation(v).slice(0, 20),
                }));
              }}
            />
            <Field
              id="profile-lastName"
              label="Last Name"
              value={form.lastName}
              editing={editing}
              required
              maxLength={20}
              error={nameErrors?.lastName}
              onChange={(v) => {
                onClearNameError?.('lastName');
                setForm((f) => ({
                  ...f,
                  lastName: inputValidation(v).slice(0, 20),
                }));
              }}
            />
          </>
        )}
        <Field label="Email" value={form.email} editing={false} disabled />
        <div>
          <label className="mb-1 block text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
            Phone
          </label>
          {editing ? (
            <div className="flex h-[44px] overflow-hidden rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)]">
              <span className="flex h-full shrink-0 items-center border-r border-[var(--dash-line)] bg-white/70 px-3 text-sm font-semibold text-[var(--dash-ink)]">
                +91
              </span>
              <input
                value={formatMobileDisplay(form.mobileNumber, mobileConfig)}
                onChange={(e) => handleMobileChange(e.target.value)}
                type="tel"
                inputMode="numeric"
                maxLength={mobileConfig.placeholder.length}
                className="h-full min-w-0 flex-1 border-none bg-transparent px-3 text-sm font-semibold text-[var(--dash-ink)] outline-none placeholder:text-[var(--dash-muted)]"
                placeholder={mobileConfig.placeholder}
              />
            </div>
          ) : (
            <p className="m-0 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)]/60 px-3 py-2.5 text-sm font-semibold text-[var(--dash-ink)]">
              {formatMobileWithCountryCode(form.mobileNumber, form.countryCode) ||
                '—'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
